/*
  Returns a Promise that resolves once every element in `elements` has been
  observed as mutated (or already removed from the DOM).

  Usage:
    const done = waitForElementsToBeRemovedFromDom({ container, elements });
    await renderer.removeHighlight({ elements, rangyHighlight });
    await done;

  The observer must be set up before removal begins so it catches all
  mutations. Each element may generate multiple MutationObserver events
  (e.g. child.remove() for resize handles, then undoToRange for content
  extraction), so we track distinct elements via a Set, not a count.
*/
export default function waitForElementsToBeRemovedFromDom({
  container,
  elements,
}: {
  container: HTMLElement;
  elements: HTMLElement[];
}): Promise<void> {
  let elementsToTrack = Array.from(elements);
  const totalToRemove = elementsToTrack.length;
  const isContainerDetached = !document.body.contains(container);

  if (!totalToRemove || isContainerDetached) {
    return Promise.resolve();
  }

  const elementsRemoved = new Set<Node>();

  return new Promise<void>((resolve) => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- observer is set with childList:true on an HTMLElement container, so mutation.target is always an HTMLElement
        if (elementsToTrack.includes(mutation.target as HTMLElement)) {
          elementsRemoved.add(mutation.target);
        }
      }

      const done = () => {
        observer.disconnect();
        resolve();
      };

      if (elementsRemoved.size >= totalToRemove) {
        done();
        return;
      }

      /*
        Check if the remaining elements have already left the DOM without
        the observer catching them. Seen with video documents.
      */
      const elementStillInDom: HTMLElement[] = [];
      for (const element of elementsToTrack) {
        if (document.contains(element)) {
          elementStillInDom.push(element);
        } else {
          elementsRemoved.add(element);
        }
      }
      elementsToTrack = elementStillInDom;

      if (elementsRemoved.size >= totalToRemove) {
        done();
      }
    });

    observer.observe(container, { childList: true, subtree: true });
  });
}
